import React from "react"

import Window from "../../structure/window/window"
import WindowCard from "../../ui/window-card/window-card"
import Button from "../../ui/button/button"
import Badge from "../../ui/badge/badge"
import { CopyIcon } from "../../../utils/svgs"

import styles from "./payment-link-detail.module.scss"

const formatGbp = (value) =>
	Number(value || 0).toLocaleString("en-GB", {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	})

const STATUS_META = {
	ACTIVE: { label: "Awaiting payment", type: "AMBER" },
	PAID: { label: "Paid", type: "POSITIVE" },
	ALLOCATED: { label: "Allocated", type: "INFO" },
	CANCELLED: { label: "Cancelled", type: "" },
	AWAITING: { label: "Awaiting payment", type: "AMBER" },
}

/**
 * Detail modal for a payment link. Used by the Payment Links report (and
 * reusable elsewhere when we want a focused view of a single link).
 *
 * `row` is the enriched row object from the report's `fetchReport()` —
 * carries sentAt/paidAt/cancelledAt as moments, plus the raw doc data.
 * Resend/Cancel actions only render for client-level links in ACTIVE state.
 */
export default function PaymentLinkDetail({ row, busy, busyAction, onCopy, onResend, onCancel, close }) {
	const statusMeta = STATUS_META[row.status] || STATUS_META.ACTIVE
	const canManage = row.status === "ACTIVE" && row.parentType === "clients"

	return (
		<Window
			title="Payment link"
			className="slim"
			close={() => close()}>
			<WindowCard>
				<div className={styles.headRow}>
					<div>
						<p className={styles.headLabel}>Amount</p>
						<p className={styles.headAmount}>£{formatGbp(row.amount)}</p>
					</div>
					<div className={styles.statusStack}>
						{row.status === "ALLOCATED" && (
							<Badge
								type={STATUS_META.PAID.type}
								label={STATUS_META.PAID.label}
							/>
						)}
						<Badge
							type={statusMeta.type}
							label={statusMeta.label}
						/>
					</div>
				</div>

				<div className={styles.grid}>
					<Field
						label="Payee"
						value={row.payee || "—"}
					/>
					<Field
						label="Recipient"
						value={row.recipient || "—"}
					/>
					<Field
						label="Sent"
						value={row.sentAt ? row.sentAt.format("DD MMM YYYY [at] HH:mm") : "—"}
					/>
					<Field
						label="Paid"
						value={row.paidAt ? row.paidAt.format("DD MMM YYYY [at] HH:mm") : "—"}
					/>
					<Field
						label="Sent by"
						value={row.sentBy || "—"}
					/>
					<Field
						label="Send count"
						value={`${row.sendCount}${row.sendCount > 1 && row.lastSentAt ? ` (last ${row.lastSentAt.fromNow()})` : ""}`}
					/>
					{row.cancelledAt && (
						<Field
							label="Cancelled at"
							value={row.cancelledAt.format("DD MMM YYYY [at] HH:mm")}
						/>
					)}
					{row.note && (
						<Field
							label="Note"
							value={row.note}
							wide
						/>
					)}
					{row.url && (
						<Field
							label="Stripe link"
							value={
								<a
									href={row.url}
									target="_blank"
									rel="noopener noreferrer">
									{row.url}
								</a>
							}
							wide
						/>
					)}
				</div>

				{canManage && (
					<div className={styles.actions}>
						<Button
							label="Copy link"
							icon={<CopyIcon />}
							type="secondary"
							small={true}
							onClick={onCopy}
							disabled={busy}
						/>
						<Button
							label="Resend email"
							type="secondary"
							small={true}
							loading={busyAction === "resend"}
							loadingText="Sending..."
							disabled={busy && busyAction !== "resend"}
							onClick={onResend}
						/>
						<Button
							label="Cancel link"
							small={true}
							loading={busyAction === "cancel"}
							loadingText="Cancelling..."
							disabled={busy && busyAction !== "cancel"}
							onClick={onCancel}
						/>
					</div>
				)}
			</WindowCard>
		</Window>
	)
}

function Field({ label, value, wide }) {
	return (
		<div className={[styles.field, wide ? styles.fieldWide : ""].join(" ")}>
			<p className={styles.fieldLabel}>{label}</p>
			<p className={styles.fieldValue}>{value}</p>
		</div>
	)
}
